/**
 * Bundle Builder — Alpine component.
 *
 * Two components:
 *   bundleBuilder  — tabs, colour selection, fabric filtering, media slider, bundle tray
 *   bundleProgress — the tier meter and its celebration animation (its own section)
 *
 * They share nothing but window events, so either section can be reordered or removed in the
 * theme editor. Nothing is written to the cart here; the tray is projected pricing only and the
 * sticky bar performs a single cart/add.js when the shopper commits.
 *
 * State is keyed per tab, so every panel is independent — a panel is not just a view of the
 * active tab. Scoped per section id so more than one builder can live on a page. The legacy
 * collection-pdp-loader.js / collection-pdp-filter.js are intentionally not used — both are
 * document-scoped singletons and would fight each other across tabs.
 */
(() => {
  if (window.bundleBuilderLoaded) return;
  window.bundleBuilderLoaded = true;

  const ALL_FABRICS = 'all';
  const PLAYER_SRC = 'https://cdn.jsdelivr.net/npm/@dotlottie/player-component@2.7.12/dist/dotlottie-player.mjs';

  const STORAGE_PREFIX = 'bundle-builder:';
  // Long enough to survive a reload, a closed tab or a night's sleep; short enough that a bundle
  // priced weeks ago never reappears.
  const STORAGE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
  // A stored quantity is shopper input that has been outside our control, so it is clamped.
  const MAX_STORED_QTY = 20;

  const EMPTY_COLOR = {
    id: 0,
    color: '',
    hex: '#ffffff',
    description: '',
    images: [],
    available: false,
    price: 0,
    priceFormatted: '',
    embroidery: { id: 0, available: false, priceFormatted: '' },
  };

  /**
   * Fills {{ token }} or [token] placeholders from `values`, leaving anything unrecognised alone.
   * Locale strings use the braced form; merchant-facing settings are documented with [token]
   * because a literal {{ }} in a schema string reads as Liquid to theme-check. Both are accepted
   * everywhere, so a merchant pasting either gets what they expect.
   */
  const interpolate = (template, values) =>
    String(template || '').replace(/\{\{\s*(\w+)\s*\}\}|\[(\w+)\]/g, (match, braced, bracketed) => {
      const token = braced || bracketed;
      return Object.prototype.hasOwnProperty.call(values, token) ? values[token] : match;
    });

  const formatMoney = (cents, format) => {
    const value = Number(cents) || 0;

    const withDelimiters = (amount, decimals, thousands = ',', decimal = '.') => {
      const fixed = (amount / 100).toFixed(decimals);
      const [whole, fraction] = fixed.split('.');
      const grouped = whole.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return fraction ? `${grouped}${decimal}${fraction}` : grouped;
    };

    if (!format) return withDelimiters(value, 2);

    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, token) => {
      switch (token) {
        case 'amount':
          return withDelimiters(value, 2);
        case 'amount_no_decimals':
          return withDelimiters(value, 0);
        case 'amount_with_comma_separator':
          return withDelimiters(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withDelimiters(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return withDelimiters(value, 2, "'");
        case 'amount_no_decimals_with_space_separator':
          return withDelimiters(value, 0, ' ');
        case 'amount_with_space_separator':
          return withDelimiters(value, 2, ' ', ',');
        default:
          return match;
      }
    });
  };

  const todayISO = () => {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${now.getFullYear()}-${month}-${day}`;
  };

  const formatShipDate = (iso) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
    if (!match) return '';

    const [, year, month, day] = match;
    return `${month}/${day}/${year.slice(-2)}`;
  };

  const isFutureDate = (iso, today) =>
    /^\d{4}-\d{2}-\d{2}$/.test(String(iso || '')) && String(iso) >= (today || todayISO());

  const FOCUSABLE_SELECTOR =
    'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])';

  const focusWhenVisible = (element, isStale, framesLeft = 30) => {
    if (typeof isStale === 'function' && isStale()) return;

    element.focus();

    if (document.activeElement === element || framesLeft <= 0) return;

    window.requestAnimationFrame(() => focusWhenVisible(element, isStale, framesLeft - 1));
  };

  /**
   * Listener bookkeeping shared by all three components. Alpine calls destroy() when a component's
   * element leaves the DOM, which the theme editor does on every section re-render — without a
   * teardown each rebuild strands its listeners, observers and Swiper instances on detached nodes.
   */
  const listeners = {
    cleanups: null,

    on(target, type, handler, options) {
      if (!this.cleanups) this.cleanups = [];

      target.addEventListener(type, handler, options);
      this.cleanups.push(() => target.removeEventListener(type, handler, options));
    },

    releaseListeners() {
      (this.cleanups || []).forEach((off) => off());
      this.cleanups = null;
    },
  };

  const destroySwiper = (swiper) => {
    if (swiper && !swiper.destroyed) swiper.destroy(true, true);
  };

  const prefersReducedMotion = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const whenSwiperReady = () =>
    new Promise((resolve) => {
      if (window.Swiper) {
        resolve(window.Swiper);
        return;
      }

      let framesLeft = 300; 

      const tick = () => {
        if (window.Swiper) {
          resolve(window.Swiper);
        } else if (framesLeft-- > 0) {
          window.requestAnimationFrame(tick);
        } else {
          resolve(null);
        }
      };

      window.requestAnimationFrame(tick);
    });

  let playerPromise = null;

  /**
   * Loads the dotLottie web component once, on first celebration.
   *
   * lottie-web cannot be used here: a .lottie file is a ZIP archive (manifest.json +
   * animations/*.json) and lottie-web only accepts raw Lottie JSON. This player reads both
   * formats, so merchants can drop in either. Resolves false if the CDN cannot be reached.
   */
  const loadPlayer = () => {
    if (window.customElements && window.customElements.get('dotlottie-player')) {
      return Promise.resolve(true);
    }

    if (!playerPromise) {
      playerPromise = import(PLAYER_SRC)
        .then(() => true)
        .catch(() => false);
    }

    return playerPromise;
  };

  const bundleBuilder = (sectionId) => ({
    ...listeners,
    sectionId,
    strings: {},
    settings: {},
    tiers: [],
    tabs: [],
    activeTab: '',
    selection: {},
    fabricFilter: {},
    bundle: {},
    pending: {},
    cartLines: [],
    personalizeFor: null,
    swipers: {},
    barObserver: null,
    discountPercent: 0,
    nextTier: null,
    tierBarLabel: '',
    tierSteps: [],
    tierFillPercent: 0,
    justAdded: '',
    isAdding: false,
    cartError: '',
    // Starts closed so landing on the page keeps the product module in view; init() opens it again
    // when a bundle was restored, and pushLine() opens it on the first pick.
    barCollapsed: true,
    notifyTabKey: '',
    notifyEmail: '',
    notifyStatus: 'idle',
    notifyReturnFocus: null,

    init() {
      const payload = document.getElementById(`bundle-builder-data-${this.sectionId}`);

      if (!payload) return;

      try {
        const data = JSON.parse(payload.textContent);
        this.strings = data.strings || {};
        this.tabs = data.tabs || [];
        this.settings = data.settings || {};
      } catch (error) {
        this.tabs = [];
        return;
      }

      if (!this.tabs.length) return;

      this.activeTab = this.tabs[0].key;
      this.tabs.forEach((tab) => {
        const firstAvailable = tab.colors.find((color) => color.available) || tab.colors[0];
        if (firstAvailable) this.selection[tab.key] = firstAvailable.id;
        this.fabricFilter[tab.key] = ALL_FABRICS;
      });

      this.restoreBundle();
      if (this.itemCount > 0) this.barCollapsed = false;

      // Anything that touches the cart — this bar, a PDP, the Rebuy drawer — announces it on this
      // event, so the meter re-reads rather than drifting out of sync with what the shopper owns.
      this.on(document, 'theme:cartchanged', () => this.loadCartLines());
      this.loadCartLines();

      this.on(window, 'bundle-builder:apply-configuration', (event) => {
        this.applyConfiguration(event.detail);
      });

      this.on(window, 'bundle-builder:tier', (event) => {
        const detail = event.detail || {};
        this.discountPercent = detail.percent || 0;
        this.nextTier = detail.next || null;
        this.tierBarLabel = detail.barLabel || '';
        this.tierSteps = detail.steps || [];
        this.tierFillPercent = detail.fillPercent || 0;
      });

      this.on(window, 'bundle-builder:request-state', () => this.publish());

      // Sent by the embroidery visualizer once the shopper finishes personalising.
      this.on(document, 'bundle-builder:embroidery-add', (event) => {
        this.addPersonalizedToBundle(event.detail);
      });

      this.$nextTick(() => {
        this.mountSliders();
        this.reserveBarSpace();
        this.publish();
      });
    },

    /* ---------------------------------------------------------------- lookups */

    tabByKey(tabKey) {
      return this.tabs.find((tab) => tab.key === tabKey);
    },

    colorById(variantId) {
      for (const tab of this.tabs) {
        const match = tab.colors.find((color) => color.id === variantId);
        if (match) return match;
      }
      return null;
    },

    /**
     * Resolves a cart line's variant back to one of ours. Both the plain colour and its embroidery
     * variant count, and everything else in the cart is ignored on purpose — a cover or a gift
     * card must not push the shopper into a tier they have not earned.
     */
    colorByVariantId(variantId) {
      for (const tab of this.tabs) {
        const match = tab.colors.find(
          (color) => color.id === variantId || (color.embroidery && color.embroidery.id === variantId)
        );
        if (match) return { colorId: match.id, tabKey: tab.key };
      }
      return null;
    },

    colorFor(tabKey) {
      const tab = this.tabByKey(tabKey);
      if (!tab) return EMPTY_COLOR;

      const selected = this.selection[tabKey];
      return tab.colors.find((color) => color.id === selected) || tab.colors[0] || EMPTY_COLOR;
    },

    get activeColor() {
      return this.colorFor(this.activeTab);
    },

    /**
     * Prints and multi-colour options (Crayola, Dinos) are a swatch image, not a hex — a flat dot
     * renders them white. Mirror the swatch when there is one.
     */
    colorDotStyle(tabKey) {
      const color = this.colorFor(tabKey);

      return color.swatch ? `background-image: url(${color.swatch})` : `background-color: ${color.hex}`;
    },

    /* ------------------------------------------------------------------ media */

    imagesFor(tabKey) {
      return this.colorFor(tabKey).images || [];
    },

    imageAltFor(tabKey) {
      const tab = this.tabByKey(tabKey);
      const color = this.colorFor(tabKey);
      const title = (tab && tab.title) || '';
      return color.color ? `${title} — ${color.color}` : title;
    },

    async mountSliders() {
      const Swiper = await whenSwiperReady();
      if (!Swiper) return;

      this.tabs.forEach((tab) => {
        const el = document.getElementById(`bundle-swiper-${this.sectionId}-${tab.key}`);
        if (!el || this.swipers[tab.key]) return;

        this.swipers[tab.key] = new Swiper(el, {
          slidesPerView: 1,
          speed: prefersReducedMotion() ? 0 : 420,
          observer: true,
          observeParents: true,
          watchOverflow: true,
          grabCursor: true,
          keyboard: { enabled: true, onlyInViewport: true },
          a11y: {
            prevSlideMessage: this.strings.previousImage || 'Previous image',
            nextSlideMessage: this.strings.nextImage || 'Next image',
          },
          pagination: false,
          navigation: {
            prevEl: `#bundle-prev-${this.sectionId}-${tab.key}`,
            nextEl: `#bundle-next-${this.sectionId}-${tab.key}`,
          },
        });
      });
    },

    resetSlider(tabKey) {
      const swiper = this.swipers[tabKey];
      if (!swiper || swiper.destroyed) return;

      this.$nextTick(() => {
        swiper.update();
        swiper.slideTo(0, prefersReducedMotion() ? 0 : 300);
      });
    },

    /* ----------------------------------------------------------------- fabrics */

    fabricsFor(tabKey) {
      const tab = this.tabByKey(tabKey);
      if (!tab) return [];

      const counts = new Map();

      tab.colors.forEach((color) => {
        (color.fabrics || []).forEach((fabric) => {
          const key = String(fabric).toLowerCase();
          const existing = counts.get(key);

          if (existing) {
            existing.count += 1;
          } else {
            counts.set(key, {
              value: key,
              label: String(fabric).replace(/_/g, ' '),
              count: 1,
            });
          }
        });
      });

      return [
        { value: ALL_FABRICS, label: 'All', count: tab.colors.length },
        ...counts.values(),
      ];
    },

    activeFabricFor(tabKey) {
      return this.fabricFilter[tabKey] || ALL_FABRICS;
    },

    setFabric(tabKey, fabricValue) {
      this.fabricFilter[tabKey] = fabricValue;

      if (!this.isColorVisible(tabKey, this.colorFor(tabKey).id)) {
        const tab = this.tabByKey(tabKey);
        const firstVisible = tab && tab.colors.find((color) => this.isColorVisible(tabKey, color.id));
        if (firstVisible) this.selectColor(tabKey, firstVisible.id);
      }
    },

    isColorVisible(tabKey, variantId) {
      const active = this.activeFabricFor(tabKey);
      if (active === ALL_FABRICS) return true;

      const color = this.colorById(variantId);
      if (!color) return false;

      return (color.fabrics || []).some((fabric) => String(fabric).toLowerCase() === active);
    },

    /* -------------------------------------------------------------------- tabs */

    setTab(tabKey) {
      if (this.activeTab === tabKey) return;

      this.activeTab = tabKey;
      this.resetSlider(tabKey);
    },

    focusTab(direction) {
      const index = this.tabs.findIndex((tab) => tab.key === this.activeTab);
      if (index === -1) return;

      const next = this.tabs[(index + direction + this.tabs.length) % this.tabs.length];
      this.setTab(next.key);

      this.$nextTick(() => {
        const button = document.getElementById(`bundle-tab-${this.sectionId}-${next.key}`);
        if (button) button.focus();
      });
    },

    selectColor(tabKey, variantId) {
      if (this.selection[tabKey] === variantId) return;

      this.selection[tabKey] = variantId;
      this.resetSlider(tabKey);
    },

    /* ------------------------------------------------------------------ bundle */

    addToBundle(tabKey) {
      const color = this.colorFor(tabKey);
      if (!color.available) return;

      this.pushLine({ tabKey, colorId: color.id, variantId: color.id });
    },

    /**
     * One tray line. Personalised lines are a different variant (the embroidery variant) and carry
     * line-item properties, so the key is variant + properties: two Smores with different
     * embroidery text must stay separate lines, while a repeat of the same one increments.
     */
    pushLine({ tabKey, colorId, variantId, properties = null }) {
      const key = properties ? `${variantId}:${JSON.stringify(properties)}` : String(variantId);
      const existing = this.bundle[key];

      if (existing) {
        existing.qty += 1;
      } else {
        this.bundle[key] = { key, id: variantId, colorId, tabKey, qty: 1, properties };
      }

      if ((this.pending[tabKey] || 0) > 0) {
        this.pending[tabKey] -= 1;
        if (this.pending[tabKey] <= 0) delete this.pending[tabKey];
      }

      // A collapsed bar would swallow the only feedback that the item landed, so it reopens.
      this.barCollapsed = false;

      this.justAdded = tabKey;
      window.setTimeout(() => {
        if (this.justAdded === tabKey) this.justAdded = '';
      }, 900);

      this.saveBundle();
      this.publish();
      this.revealChip(key);
      this.advanceToNextPendingTab(tabKey);
    },

    /* --------------------------------------------------------------- personalize */

    /**
     * Hands the selected colour to the embroidery visualizer (sections/embroidery.liquid in bundle
     * mode). The colour is remembered here because the visualizer only reports back a variant id
     * and its properties.
     */
    personalize(tabKey) {
      const color = this.colorFor(tabKey);
      if (!color.available) return;
      if (!color.embroidery || !color.embroidery.available) return;

      this.personalizeFor = { tabKey, colorId: color.id };

      document.dispatchEvent(
        new CustomEvent('bundle-builder:personalize', { detail: color.visualizer || {} })
      );
    },

    addPersonalizedToBundle(detail) {
      if (!detail || !detail.variantId) return;

      const target = this.personalizeFor || { tabKey: this.activeTab, colorId: this.activeColor.id };

      this.pushLine({
        tabKey: target.tabKey,
        colorId: target.colorId,
        variantId: detail.variantId,
        properties: detail.properties || {},
      });
    },

    removeFromBundle(key) {
      const entry = this.bundle[key];
      if (!entry) return;

      if (entry.qty > 1) {
        entry.qty -= 1;
      } else {
        delete this.bundle[key];
      }

      this.saveBundle();
      this.publish();
    },

    /* ------------------------------------------------------------- stock state */

    stockStateFor(color) {
      const stock = color && color.stock;
      if (!stock || !color.available) return '';
      if (!stock.requiresShipping || !stock.tracked) return '';

      if (stock.preorderDate && stock.quantity > 1) return 'preorder';
      if (stock.quantity < 1) return 'backorder';
      return '';
    },

    get shopToday() {
      return this.settings.today || todayISO();
    },

    stockMessageFor(tabKey) {
      if (!this.settings.showStockMessage) return '';

      const color = this.colorFor(tabKey);
      const state = this.stockStateFor(color);
      if (!state) return '';

      const isPreorder = state === 'preorder';
      const date = isPreorder ? color.stock.preorderDate : color.stock.backorderDate;
      const product = color.title || '';

      if (isFutureDate(date, this.shopToday)) {
        return interpolate(isPreorder ? this.strings.preorderDated : this.strings.backorderDated, {
          product,
          date: formatShipDate(date),
        });
      }

      return interpolate(
        isPreorder ? this.strings.preorderUndated : this.strings.backorderUndated,
        { product }
      );
    },

    stockPropertiesFor(entry) {
      if (!this.settings.showStockMessage) return null;
      if (this.isPersonalizedLine(entry)) return null;

      const color = this.colorById(entry.colorId);
      const state = this.stockStateFor(color);
      if (!state) return null;

      const isPreorder = state === 'preorder';
      const date = isPreorder ? color.stock.preorderDate : color.stock.backorderDate;
      if (!isFutureDate(date, this.shopToday)) return null;

      return { [isPreorder ? 'Pre-order Ship By' : 'Backorder Ship By']: formatShipDate(date) };
    },

    /* ---------------------------------------------------------------- notify me */

    ctaStateFor(tabKey) {
      const color = this.colorFor(tabKey);
      if (color.available) return 'add';
      if (color.inventoryState === 'COMING_SOON') return 'coming_soon';
      if (color.inventoryState === 'WAITLIST_WHEN_GONE' && this.settings.notifyEnabled) return 'notify';
      return 'sold_out';
    },

    get notifyOpen() {
      return !!this.notifyTabKey;
    },

    get notifyColor() {
      return this.notifyTabKey ? this.colorFor(this.notifyTabKey) : EMPTY_COLOR;
    },

    get notifyTitle() {
      const tab = this.tabByKey(this.notifyTabKey);
      const color = this.notifyColor;
      return [tab && tab.title, color.color].filter(Boolean).join(' — ');
    },

    openNotify(tabKey) {
      // Every lock has to be balanced by exactly one unlock, so re-opening an already-open
      // modal must not take a second one.
      const wasOpen = this.notifyOpen;

      this.notifyTabKey = tabKey;
      this.notifyEmail = '';
      this.notifyStatus = 'idle';
      this.notifyReturnFocus = document.activeElement;
      // assets/scroll-lock.js — reserves the scrollbar's width so the page does not jump wider.
      if (!wasOpen) window.ScrollLock.lock('cc-popup-no-scroll');

      this.$nextTick(() => {
        const field = document.getElementById(`bundle-notify-email-${this.sectionId}`);
        if (field) focusWhenVisible(field, () => this.notifyTabKey !== tabKey);
      });
    },

    trapNotifyFocus(event) {
      if (!this.notifyOpen) return;

      const modal = document.getElementById(`bundle-notify-${this.sectionId}`);
      if (!modal) return;

      const focusable = [...modal.querySelectorAll(FOCUSABLE_SELECTOR)].filter(
        (node) => !node.disabled && node.offsetParent !== null
      );
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      const index = focusable.indexOf(active);

      if (index === -1) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
        return;
      }

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    },

    closeNotify() {
      // `@keydown.escape.window` fires this whether or not this modal is the one on screen —
      // releasing a lock we never took would unlock the page behind the visualizer.
      const wasOpen = this.notifyOpen;

      this.notifyTabKey = '';
      this.notifyStatus = 'idle';
      if (wasOpen) window.ScrollLock.unlock();

      const target = this.notifyReturnFocus;
      this.notifyReturnFocus = null;

      if (target && typeof target.focus === 'function' && target.offsetParent !== null) {
        target.focus();
        return;
      }

      const fallback = [
        ...document.querySelectorAll(`#section-id-${this.sectionId} .bundle-builder__notify`),
      ].find((node) => node.offsetParent !== null);

      if (fallback) fallback.focus();
    },

    async submitNotify() {
      if (this.notifyStatus === 'sending') return;

      const variantId = this.notifyColor.id;
      if (!this.notifyEmail || !variantId) return;
      if (!window.NuggetNotifyMe) {
        this.notifyStatus = 'error';
        return;
      }

      this.notifyStatus = 'sending';

      try {
        await window.NuggetNotifyMe.subscribe({
          email: this.notifyEmail,
          variantId,
          companyId: this.settings.klaviyoCompanyId,
        });
        this.notifyStatus = 'done';

        this.$nextTick(() => {
          const done = document.getElementById(`bundle-notify-done-${this.sectionId}`);
          if (done) focusWhenVisible(done, () => this.notifyStatus !== 'done');
        });
      } catch (error) {
        this.notifyStatus = 'error';
      }
    },

    /* ------------------------------------------------------------------ cart */

    /**
     * Eligible items the shopper has already paid into the cart. They count towards the discount
     * tier exactly like tray items do: committing a bundle and then coming back for one more
     * product must not reset the shopper to "no discount yet".
     */
    async loadCartLines() {
      try {
        const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        });

        if (!response.ok) throw new Error(`cart.js responded ${response.status}`);

        const cart = await response.json();

        this.cartLines = (cart.items || [])
          .map((item) => {
            const match = this.colorByVariantId(item.variant_id);
            return match ? { ...match, qty: item.quantity } : null;
          })
          .filter(Boolean);
      } catch (error) {
        // A cart we cannot read simply contributes nothing to the meter.
        this.cartLines = [];
      }

      this.publish();
    },

    get cartCount() {
      return this.cartLines.reduce((total, line) => total + line.qty, 0);
    },

    /** What the discount meter runs on: what is in the tray plus what is already in the cart. */
    get tierCount() {
      return this.itemCount + this.cartCount;
    },

    get inCartLabel() {
      const count = this.cartCount;
      if (!count) return '';

      const template = count === 1 ? this.strings.barInCartOne : this.strings.barInCartOther;
      return interpolate(template, { count });
    },

    /**
     * The line under the meter. With an empty tray the price summary is hidden, so this is also
     * the only place left to tell a shopper who already bought two that one more earns 15%.
     */
    get cartStatusLabel() {
      const parts = [this.inCartLabel];

      if (!this.itemCount && this.nextTierLabel) parts.push(this.nextTierLabel);

      return parts.filter(Boolean).join(' \u00b7 ');
    },

    /* ----------------------------------------------------------- persistence */

    get storageKey() {
      return `${STORAGE_PREFIX}${this.sectionId}`;
    },

    /**
     * The tray outlives a reload: a shopper who refreshes mid-build keeps their picks until the
     * bundle reaches the cart. Only variant ids, quantities and line properties are written —
     * prices, titles and availability are always re-read from the section payload, so a restored
     * bundle can never show stale money or a colour that has sold out since.
     */
    saveBundle() {
      const lines = Object.values(this.bundle);

      try {
        if (!lines.length) {
          window.localStorage.removeItem(this.storageKey);
          return;
        }

        window.localStorage.setItem(
          this.storageKey,
          JSON.stringify({
            savedAt: Date.now(),
            lines: lines.map(({ id, colorId, tabKey, qty, properties }) => ({
              id,
              colorId,
              tabKey,
              qty,
              properties,
            })),
          })
        );
      } catch (error) {
        // Private browsing and a full quota both throw. The tray simply stops surviving reloads.
      }
    },

    restoreBundle() {
      let stored = null;

      try {
        stored = JSON.parse(window.localStorage.getItem(this.storageKey) || 'null');
      } catch (error) {
        return;
      }

      if (!stored || !Array.isArray(stored.lines)) return;

      if (Date.now() - (stored.savedAt || 0) > STORAGE_TTL_MS) {
        try {
          window.localStorage.removeItem(this.storageKey);
        } catch (error) {
          // Nothing to do — an unwritable store is also an unclearable one.
        }
        return;
      }

      stored.lines.forEach((line) => {
        const color = this.colorById(line.colorId);
        if (!color || !this.tabByKey(line.tabKey)) return;

        // Re-validated against today's payload: variant ids move when a collection is re-cut, and
        // a colour that sold out while the bundle sat in storage must not come back.
        const personalized = !!line.properties;
        const embroidery = color.embroidery || {};
        const variantId = personalized ? embroidery.id : color.id;

        if (!variantId || variantId !== line.id) return;
        if (!(personalized ? embroidery.available : color.available)) return;

        const key = personalized ? `${line.id}:${JSON.stringify(line.properties)}` : String(line.id);
        const qty = Math.min(Math.max(Math.round(Number(line.qty) || 1), 1), MAX_STORED_QTY);

        this.bundle[key] = {
          key,
          id: line.id,
          colorId: line.colorId,
          tabKey: line.tabKey,
          qty,
          properties: line.properties || null,
        };
      });
    },

    /** Total quantity of one colour, personalised lines included. */
    qtyFor(variantId) {
      return Object.values(this.bundle)
        .filter((entry) => entry.colorId === variantId)
        .reduce((total, entry) => total + entry.qty, 0);
    },

    countFor(tabKey) {
      return Object.values(this.bundle)
        .filter((entry) => entry.tabKey === tabKey)
        .reduce((total, entry) => total + entry.qty, 0);
    },

    get itemCount() {
      return Object.values(this.bundle).reduce((total, entry) => total + entry.qty, 0);
    },

    isPersonalizedLine(entry) {
      const color = this.colorById(entry.colorId);
      return !!(entry.properties && color && color.embroidery && color.embroidery.id === entry.id);
    },

    /** A personalised line is priced off the embroidery variant, which includes the add-on. */
    unitPrice(entry) {
      const color = this.colorById(entry.colorId);
      if (!color) return 0;

      if (this.isPersonalizedLine(entry)) {
        return color.embroidery.price || color.price;
      }

      return color.price;
    },

    get subtotal() {
      return Object.values(this.bundle).reduce(
        (total, entry) => total + this.unitPrice(entry) * entry.qty,
        0
      );
    },

    /* -------------------------------------------------------------- sticky bar */

    reserveBarSpace() {
      const bar = document.querySelector(`#section-id-${this.sectionId} .bundle-bar`);
      if (!bar) return;

      const apply = () => {
        document.documentElement.style.setProperty(
          '--bb-bar-height',
          `${Math.ceil(bar.getBoundingClientRect().height)}px`
        );
      };

      apply();

      if (window.ResizeObserver) {
        this.barObserver = new window.ResizeObserver(apply);
        this.barObserver.observe(bar);
      } else {
        this.on(window, 'resize', apply);
      }
    },

    /**
     * Slides the chip row to the line that just landed. Chips are appended, so from the third
     * product on the newest one sits outside the visible strip and the add reads as "nothing
     * happened" — this centres it instead.
     *
     * Manual scrollLeft rather than scrollIntoView: the bar is fixed, and scrollIntoView would
     * also try to bring it into the page's vertical view, dragging the page with it.
     */
    revealChip(key) {
      this.$nextTick(() => {
        const list = document.querySelector(`#section-id-${this.sectionId} .bundle-bar__chips`);
        if (!list) return;

        // Matched on the dataset rather than a selector: a personalised line's key carries its
        // JSON properties, which would need escaping to survive querySelector.
        const chip = Array.from(list.children).find((item) => item.dataset.chipKey === key);
        if (!chip) return;

        const listRect = list.getBoundingClientRect();
        const chipRect = chip.getBoundingClientRect();
        const offset = chipRect.left - listRect.left - (listRect.width - chipRect.width) / 2;

        list.scrollTo({
          left: Math.max(0, list.scrollLeft + offset),
          behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        });
      });
    },

    /**
     * Phone-only collapse. The handle is hidden from 750px up and every rule for .is-collapsed
     * sits inside the same media query, so a shopper who collapsed the bar and then rotated to
     * landscape still sees the full bar — no matchMedia to keep in sync here.
     */
    toggleBar() {
      this.barCollapsed = !this.barCollapsed;
    },

    get barToggleLabel() {
      return (this.barCollapsed ? this.strings.barExpand : this.strings.barCollapse) || '';
    },

    destroy() {
      this.releaseListeners();

      this.barObserver?.disconnect();
      this.barObserver = null;
      // The bar is gone, so the space it reserved has to go with it.
      document.documentElement.style.removeProperty('--bb-bar-height');

      Object.values(this.swipers).forEach(destroySwiper);
      this.swipers = {};
    },



    get trayEntries() {
      return Object.values(this.bundle).map((line) => {
        const tab = this.tabByKey(line.tabKey);
        const color = this.colorById(line.colorId) || EMPTY_COLOR;
        const personalized = this.isPersonalizedLine(line);
        const parts = [(tab && tab.label) || '', color.color];

        if (personalized) parts.push(this.strings.personalized || '');

        const state = personalized ? '' : this.stockStateFor(color);
        if (state === 'backorder') parts.push(this.strings.chipBackorder || '');
        if (state === 'preorder') parts.push(this.strings.chipPreorder || '');

        return {
          key: line.key,
          qty: line.qty,
          color,
          label: parts.filter(Boolean).join(' \u2022 '),
        };
      });
    },

    get pendingEntries() {
      return this.tabs
        .filter((tab) => this.pendingFor(tab.key) > 0)
        .map((tab) => ({
          tabKey: tab.key,
          label: interpolate(this.strings.pendingSlot, {
            count: this.pendingFor(tab.key),
            product: tab.label,
          }),
        }));
    },

    get savings() {
      return Math.round((this.subtotal * this.discountPercent) / 100);
    },

    get total() {
      return this.subtotal - this.savings;
    },

    get subtotalFormatted() {
      return formatMoney(this.subtotal, this.strings.moneyFormat);
    },

    get totalFormatted() {
      return formatMoney(this.total, this.strings.moneyFormat);
    },

    /**
     * "Add 1 more for 10% off" under the bar price. The tiers live in the progress section, so the
     * next one arrives on its bundle-builder:tier event; with that section absent this stays blank.
     */
    get nextTierLabel() {
      if (!this.nextTier || !this.nextTier.remaining) return '';

      return interpolate(this.strings.barNextTier, {
        count: this.nextTier.remaining,
        percent: this.nextTier.percent,
      });
    },

    /**
     * "Save $120" next to the bar price, or the reached tier's own copy when it sets one — the
     * "Savings message in sticky bar" field on its block, which arrives on the
     * bundle-builder:tier event. Falls back to the shared string when a tier leaves it blank or
     * the progress section is not on the page.
     */
    get savingsLabel() {
      return interpolate(this.tierBarLabel || this.strings.barSave, {
        amount: formatMoney(this.savings, this.strings.moneyFormat),
      });
    },

    /** @param {string} idleLabel Copy to use when there is nothing outstanding. */
    submitLabelFor(idleLabel) {
      if (this.isAdding) return this.strings.barAdding || '';

      const outstanding = this.totalPending;

      if (outstanding > 0) {
        const template =
          outstanding === 1 ? this.strings.barPendingOne : this.strings.barPendingOther;
        return interpolate(template, { count: outstanding });
      }

      return idleLabel || '';
    },

    get submitLabel() {
      return this.submitLabelFor(this.strings.barSubmit);
    },

    /**
     * Sends the whole bundle in one request. Payload shape and the post-add events match the
     * legacy bundle tool (sections/collection-bundle-tool.liquid) so the Rebuy drawer and any
     * analytics listeners behave identically.
     */
    async addBundleToCart() {
      if (this.isAdding || !this.itemCount || this.totalPending > 0) return;

      this.isAdding = true;
      this.cartError = '';

      const items = Object.values(this.bundle).map((line) => {
        const item = { id: line.id, quantity: line.qty };
        const properties = { ...(line.properties || {}), ...(this.stockPropertiesFor(line) || {}) };

        if (Object.keys(properties).length) item.properties = properties;
        return item;
      });

      try {
        const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items }),
        });

        if (!response.ok) throw new Error(`cart/add.js responded ${response.status}`);

        // Emptied before anything else: the bundle now lives in the cart, and the non-drawer
        // branch below navigates away, which is not a good moment to still be holding a saved copy.
        this.bundle = {};
        this.saveBundle();
        this.publish();

        document.dispatchEvent(
          new CustomEvent('theme:cartchanged', { bubbles: true, cancelable: false })
        );

        if (window.theme && window.theme.settings && window.theme.settings.cart_type === 'drawer') {
          document.dispatchEvent(
            new CustomEvent('theme:open-cart-drawer', { bubbles: true, cancelable: false })
          );
        } else {
          window.location = `${window.Shopify.routes.root}cart`;
        }
      } catch (error) {
        this.cartError = this.strings.barError || '';
      } finally {
        this.isAdding = false;
      }
    },

    publish() {
      window.dispatchEvent(
        new CustomEvent('bundle-builder:state', {
          detail: {
            sectionId: this.sectionId,
            // The tier is resolved from tray + cart; the money stays the tray's own, because that
            // is what this "Add bundle to cart" is about to charge.
            itemCount: this.tierCount,
            subtotal: this.subtotal,
          },
        })
      );
    },

    pendingFor(tabKey) {
      return this.pending[tabKey] || 0;
    },

    get totalPending() {
      return Object.values(this.pending).reduce((total, count) => total + count, 0);
    },

    pendingLabel(tabKey) {
      return interpolate(this.strings.pending, { count: this.pendingFor(tabKey) });
    },

    addLabelFor(tabKey) {
      const color = this.colorFor(tabKey);

      if (!color.available) {
        return color.inventoryState === 'COMING_SOON'
          ? this.strings.comingSoon || this.strings.soldOut || ''
          : this.strings.soldOut || '';
      }

      const stillNeeded = this.pendingFor(tabKey);
      if (stillNeeded > 0) return interpolate(this.strings.addMore, { count: stillNeeded });

      if (this.qtyFor(color.id) > 0) {
        return interpolate(this.strings.added || this.strings.add, { color: color.color });
      }

      if (this.settings.showStockOnButton) {
        const state = this.stockStateFor(color);
        if (state === 'backorder') return this.strings.addBackorder || this.strings.add || '';
        if (state === 'preorder') return this.strings.addPreorder || this.strings.add || '';
      }

      return this.strings.add || '';
    },

    /**
     * Applies a Bundle Inspiration preset: reserve a slot per product, then send the shopper
     * to the first tab that still needs a colour.
     * @param {{ config: Array<{ tab: string, quantity: number }> }} detail
     */
    applyConfiguration(detail) {
      if (!detail || !Array.isArray(detail.config)) return;

      this.pending = {};

      detail.config.forEach(({ tab, quantity }) => {
        const match = this.tabs.find((entry) => entry.key === tab || entry.label === tab);
        if (!match || !quantity) return;
        this.pending[match.key] = (this.pending[match.key] || 0) + Number(quantity);
      });

      const firstNeeded = this.tabs.find((tab) => this.pendingFor(tab.key) > 0);

      this.barCollapsed = false;

      if (firstNeeded) {
        this.setTab(firstNeeded.key);
        const root = document.getElementById(`section-id-${this.sectionId}`);
        if (root) root.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },

    advanceToNextPendingTab(fromTab) {
      if (this.pendingFor(fromTab) > 0) return;

      const next = this.tabs.find((tab) => tab.key !== fromTab && this.pendingFor(tab.key) > 0);

      if (next) window.setTimeout(() => this.setTab(next.key), 350);
    }
  });

  const bundleProgress = (sectionId) => ({
    ...listeners,
    sectionId,
    strings: {},
    tiers: [],
    animationMode: 'meter',
    itemCount: 0,
    subtotal: 0,
    lastTierIndex: 0,
    hasReceivedState: false,
    celebrationToken: 0,
    celebrationTimer: null,

    init() {
      const payload = document.getElementById(`bundle-progress-data-${this.sectionId}`);

      if (!payload) return;

      try {
        const data = JSON.parse(payload.textContent);
        this.strings = data.strings || {};
        this.animationMode = data.animationMode || 'meter';
        this.tiers = (data.tiers || []).slice().sort((a, b) => a.items - b.items);
      } catch (error) {
        this.tiers = [];
        return;
      }

      this.on(window, 'bundle-builder:state', (event) => {
        const detail = event.detail || {};
        this.itemCount = detail.itemCount || 0;
        this.subtotal = detail.subtotal || 0;

        // The builder's opening broadcast can already carry a bundle restored from a previous
        // visit. That is not a tier the shopper just unlocked, so the first state only primes the
        // meter — otherwise a reload would greet them with the celebration again.
        const isFirstState = !this.hasReceivedState;
        this.hasReceivedState = true;
        this.syncTier({ celebrate: !isFirstState });
      });

      window.dispatchEvent(new CustomEvent('bundle-builder:request-state'));

      this.syncTier({ celebrate: false });
    },

    /* ------------------------------------------------------------------- tiers */

    get tierIndex() {
      let index = 0;
      this.tiers.forEach((tier, position) => {
        if (this.itemCount >= tier.items) index = position;
      });
      return index;
    },

    get currentTier() {
      return this.tiers[this.tierIndex] || { items: 0, percent: 0, applied_label: '' };
    },

    get nextTier() {
      return this.tiers.find((tier) => tier.items > this.itemCount) || null;
    },

    /**
     * Whether the shopper actually qualifies for `currentTier`. tierIndex falls back to 0, so
     * without this a tier list that has no 0-item entry would report its first discount at an
     * empty bundle — and that percent is broadcast to the sticky bar's pricing.
     */
    get tierReached() {
      return this.itemCount >= (this.currentTier.items || 0);
    },

    get discountPercent() {
      return this.tierReached ? this.currentTier.percent || 0 : 0;
    },

    get maxTierItems() {
      return this.tiers.length ? this.tiers[this.tiers.length - 1].items : 0;
    },

    get meterSteps() {
      return this.tiers.map((tier) => ({
        label: tier.label,
        reached: this.itemCount >= tier.items,
        showCheck: this.itemCount >= tier.items && tier.items > 0,
      }));
    },

    get meterPercent() {
      if (this.tiers.length < 2) return this.itemCount > 0 ? 100 : 0;

      const last = this.tiers.length - 1;
      const index = this.tierIndex;

      if (index >= last) return 100;

      const from = this.tiers[index].items;
      const to = this.tiers[index + 1].items;
      const span = to - from;
      const progress = span > 0 ? (this.itemCount - from) / span : 0;

      return Math.min(100, ((index + progress) / last) * 100);
    },

    get meterStatus() {
      const current = this.currentTier;

      /* A tier's own message takes over as soon as it is reached. The 0-item tier is the
         exception: it doubles as the empty state, so its message only stands while the bundle is
         empty — with one item in, the shopper gets the automatic countdown to the first discount
         (bundle_builder.meter.next_one/next_other) instead. */
      if (this.tierReached && current.applied_label && (current.items > 0 || this.itemCount === 0)) {
        return current.applied_label;
      }

      if (this.discountPercent > 0) return '';

      if (this.nextTier) {
        const remaining = this.nextTier.items - this.itemCount;
        const template = remaining === 1 ? this.strings.nextOne : this.strings.nextOther;

        return interpolate(template, { count: remaining, percent: this.nextTier.percent });
      }

      return '';
    },

    syncTier({ celebrate = true } = {}) {
      const index = this.tierIndex;

      const current = this.currentTier;
      const next = this.nextTier;

      window.dispatchEvent(
        new CustomEvent('bundle-builder:tier', {
          detail: {
            sectionId: this.sectionId,
            index,
            percent: this.discountPercent,
            barLabel: this.tierReached ? current.bar_label || '' : '',
            // The bar draws its own compact copy of the meter from these, so the tier maths stays
            // here rather than being reimplemented against a second copy of the data.
            fillPercent: this.meterPercent,
            steps: this.meterSteps,
            next: next
              ? {
                  items: next.items,
                  percent: next.percent,
                  remaining: Math.max(0, next.items - this.itemCount),
                }
              : null,
          },
        })
      );

      if (celebrate && index > this.lastTierIndex) {
        const tier = this.tiers[index];
        if (tier && tier.animation) this.celebrate(tier.animation);
      }

      this.lastTierIndex = index;
    },

    /**
     * Plays a tier's animation once. "meter" mode renders it over the progress card, "fullscreen"
     * over the whole viewport; "none" disables it. Reduced-motion visitors never see it.
     * @param {string} animationUrl Asset URL of a .lottie or Lottie .json, resolved in Liquid.
     */
    async celebrate(animationUrl) {
      if (this.animationMode === 'none' || prefersReducedMotion()) return;

      const loaded = await loadPlayer();
      if (!loaded) return;

      const token = (this.celebrationToken += 1);
      window.clearTimeout(this.celebrationTimer);

      const fullscreen = this.animationMode === 'fullscreen';
      const stage = fullscreen ? this.fullscreenStage() : this.$refs.stage;

      if (!stage) return;

      const player = document.createElement('dotlottie-player');
      player.setAttribute('src', animationUrl);
      player.setAttribute('autoplay', '');
      player.setAttribute('renderer', 'svg');
      player.setAttribute('background', 'transparent');
      stage.replaceChildren(player);
      stage.classList.add('is-playing');

      const cleanup = () => {
        if (token !== this.celebrationToken) return;

        window.clearTimeout(this.celebrationTimer);
        stage.classList.remove('is-playing');
        stage.replaceChildren();
        if (fullscreen) stage.remove();
      };

      player.addEventListener('complete', cleanup, { once: true });
      this.celebrationTimer = window.setTimeout(cleanup, 10000);
    },

    destroy() {
      this.releaseListeners();

      window.clearTimeout(this.celebrationTimer);
      // Invalidates any celebration still in flight, including a fullscreen stage on <body>.
      this.celebrationToken += 1;
      document
        .querySelectorAll('.bundle-builder__celebration--fullscreen')
        .forEach((stage) => stage.remove());
    },

    fullscreenStage() {
      // Two tiers can be crossed in quick succession, and the second celebration invalidates the
      // first one's cleanup token — without this its last frame stays frozen over the page.
      document
        .querySelectorAll('.bundle-builder__celebration--fullscreen')
        .forEach((orphan) => orphan.remove());

      const stage = document.createElement('div');
      stage.className = 'bundle-builder__celebration bundle-builder__celebration--fullscreen';
      stage.setAttribute('aria-hidden', 'true');
      document.body.appendChild(stage);
      return stage;
    },
  });

  const bundleTips = () => ({
    swiper: null,

    init() {
      this.mount();
    },

    destroy() {
      destroySwiper(this.swiper);
      this.swiper = null;
    },

    async mount() {
      const Swiper = await whenSwiperReady();
      const el = this.$refs.slider;
      if (!Swiper || !el || this.swiper) return;

      this.swiper = new Swiper(el, {
        slidesPerView: 'auto', 
        spaceBetween: 16,
        speed: prefersReducedMotion() ? 0 : 420,
        observer: true,
        observeParents: true,
        watchOverflow: true,
        navigation: {
          prevEl: this.$refs.prev || null,
          nextEl: this.$refs.next || null,
          disabledClass: 'swiper-button-disabled',
          lockClass: 'swiper-button-lock',
        },
        breakpoints: {
          990: { allowTouchMove: false },
        },
      });
    },
  });

  const register = () => {
    if (!window.Alpine) return;

    window.Alpine.data('bundleBuilder', bundleBuilder);
    window.Alpine.data('bundleProgress', bundleProgress);
    window.Alpine.data('bundleTips', bundleTips);
  };

  if (window.Alpine) {
    register();
  } else {
    document.addEventListener('alpine:init', register);
  }
})();

